/**
 * Billing Reports — pick a report + date range, preview + CSV export.
 */
import { useCallback, useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, Loader2 } from "lucide-react";
import BillingTabs from "@/components/app/BillingTabs";
import { DateDMYInput } from "@/components/DateDMYInput";

const REPORTS = [
  { key: "daily_collection",   label: "Daily collection" },
  { key: "monthly_collection", label: "Monthly collection" },
  { key: "outstanding",        label: "Outstanding" },
  { key: "aging",              label: "Aging report" },
  { key: "wallet",             label: "Wallet (advances)" },
  { key: "refund",             label: "Refunds" },
  { key: "doctor_collection",  label: "Doctor collection" },
  { key: "treatment_revenue",  label: "Treatment revenue" },
  { key: "payment_method",     label: "Payment method" },
];

const fmtDate = (d) => new Date(d).toISOString().slice(0, 10);
const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;

export default function BillingReports() {
  const today = new Date();
  const thirtyAgo = new Date(today.getTime() - 30 * 24 * 3600 * 1000);
  const [kind, setKind] = useState("daily_collection");
  const [from, setFrom] = useState(fmtDate(thirtyAgo));
  const [to, setTo]     = useState(fmtDate(today));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/billing/reports/${kind}`, {
        params: { format: "json", from_date: from, to_date: to },
      });
      setReport(data);
    } catch { toast.error("Could not load report"); }
    finally { setLoading(false); }
  }, [kind, from, to]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    // Use fetch to preserve auth, then trigger download.
    try {
      const token = localStorage.getItem("token");
      const url = `${API}/billing/reports/${kind}?format=csv&from_date=${from}&to_date=${to}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${kind}_${from}_${to}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Report downloaded");
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="billing-reports">
      <BillingTabs />
      <div className="mb-6">
        <div className="overline text-ink-soft">Billing · Reports</div>
        <h1 className="mt-1 font-display text-4xl tracking-tight text-ink">
          Every number, exportable.
        </h1>
        <div className="text-sm text-ink-mid mt-1">
          9 financial reports · JSON preview + CSV export (Excel-friendly).
        </div>
      </div>

      {/* Controls */}
      <div className="rounded-2xl border border-[#E8EAE9] bg-white p-4 flex flex-wrap gap-3 items-end mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-ink-soft block mb-1">Report</label>
          <select value={kind} onChange={(e) => setKind(e.target.value)}
                  data-testid="reports-kind"
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm">
            {REPORTS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-ink-soft block mb-1">From</label>
          <DateDMYInput value={from} onChange={(e) => setFrom(e.target.value)}
                 data-testid="reports-from"
                 className="h-9 rounded-md border border-input bg-background px-3 text-sm font-mono" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-ink-soft block mb-1">To</label>
          <DateDMYInput value={to} onChange={(e) => setTo(e.target.value)}
                 data-testid="reports-to"
                 className="h-9 rounded-md border border-input bg-background px-3 text-sm font-mono" />
        </div>
        <div className="flex-1" />
        <button type="button" onClick={downloadCsv}
                data-testid="reports-download-csv"
                className="btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5">
          <Download className="w-3.5 h-3.5" /> Download CSV
        </button>
      </div>

      {/* Summary */}
      {report && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          <div className="rounded-xl border border-[#E8EAE9] bg-white px-4 py-2">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft">Rows</div>
            <div className="font-mono text-lg">{report.count}</div>
          </div>
          <div className="rounded-xl border border-[#E8EAE9] bg-white px-4 py-2">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft">Total</div>
            <div className="font-mono text-lg">{INR(report.total)}</div>
          </div>
          <div className="rounded-xl border border-[#E8EAE9] bg-white px-4 py-2">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft">Range</div>
            <div className="font-mono text-xs">{from} → {to}</div>
          </div>
        </div>
      )}

      {/* Preview table */}
      <div className="rounded-2xl border border-[#E8EAE9] bg-white overflow-hidden">
        {loading ? (
          <div className="py-14 text-center text-ink-soft"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : !report || report.rows.length === 0 ? (
          <div className="py-14 text-center text-ink-soft flex flex-col items-center gap-2">
            <FileText className="w-6 h-6 opacity-40" />
            <span className="text-sm">No rows for this range.</span>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[500px]">
            <table className="w-full text-sm" data-testid="reports-table">
              <thead className="bg-linen/50 text-[10px] uppercase tracking-widest text-ink-soft sticky top-0">
                <tr>
                  {report.columns.map((c) => (
                    <th key={c} className="px-4 py-2.5 text-left whitespace-nowrap">{c.replace(/_/g, " ")}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r, i) => {
                  // Report rows are refetched atomically, but the review flagged
                  // idx-as-key. Build a stable key from the first identifying
                  // column present in this report's schema, falling back to a
                  // short content hash so re-orderings never share DOM state.
                  const idKey = r.id || r.invoice_no || r.patient_id || r.doctor_id
                                    || r.date || `${i}-${JSON.stringify(r).slice(0, 40)}`;
                  return (
                  <tr key={idKey} className="border-t border-[#F2F0EB] hover:bg-linen/40">
                    {report.columns.map((c) => {
                      const v = r[c];
                      const isMoney = ["amount", "total", "remaining", "wallet_balance"].includes(c);
                      return (
                        <td key={c}
                            className={`px-4 py-2 whitespace-nowrap ${isMoney ? "font-mono text-right" : "text-ink"}`}>
                          {v == null ? "—" : isMoney ? INR(v) : String(v)}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
